import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useRecipe } from '../../context/RecipeContext';
import Header from '../../components/Header';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import RecipeCard from '../../components/RecipeCard';
import styles from './Saved.module.css';
import { usePageSEO } from '../../hooks/usePageSEO';

const Saved: React.FC = () => {
  return <SavedContent />
}

const SavedContent: React.FC = () => {
  // SEO hook for the saved recipes page
  usePageSEO({
    title: "My Digital Diet Profile | PlatefullAI - Your Personalized Dietary Collection",
    description: "Manage your personalized dietary profile with saved recipes that match your dietary preferences. Build your digital diet identity with recipes tailored to your nutritional needs and restrictions.",
    keywords: "digital diet profile, dietary preferences, personalized nutrition, saved recipes, dietary restrictions, food allergies, recipe personalization, nutritional profile, dietary identity, meal planning, food technology",
    canonicalUrl: "https://platefullai.com/saved"
  });

  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { fetchSaved, unsave, allUserRecipes, fetchAllUserRecipes, deleteRecipe, deleteVariant } = useRecipe();
  const [selectedRecipe, setSelectedRecipe] = useState<typeof allUserRecipes[0] | null>(null);
  const [query, setQuery] = useState('');
  const [showUnsaveDialog, setShowUnsaveDialog] = useState(false);
  const [recipeToUnsave, setRecipeToUnsave] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [recipeToDelete, setRecipeToDelete] = useState<{ id: string; type: string } | null>(null);

  useEffect(() => {
    if (user) {
      fetchAllUserRecipes();
      // Keep fetchSaved for backward compatibility with unsave functionality
      fetchSaved();
    }
  }, [user, fetchAllUserRecipes, fetchSaved]);

  console.log("allUserRecipes", allUserRecipes)

  const handleLogin = () => {
    // TODO: Navigate to login page or show login modal
    console.log('Login clicked');
  };

  const handleRecipeClick = (recipe: typeof allUserRecipes[0]) => {
    setSelectedRecipe(recipe);
  };

  const handleUnsaveClick = (recipeId: string) => {
    setRecipeToUnsave(recipeId);
    setShowUnsaveDialog(true);
  };

  const handleUnsaveConfirm = async () => {
    if (!recipeToUnsave) {
      return;
    }
    
    await unsave(recipeToUnsave);
    // Refresh both the saved recipes list and all user recipes
    if (user) {
      fetchSaved();
      fetchAllUserRecipes();
    }
    
    setShowUnsaveDialog(false);
    setRecipeToUnsave(null);
  };

  const handleUnsaveCancel = () => {
    setShowUnsaveDialog(false);
    setRecipeToUnsave(null);
  };

  const handleDeleteClick = (recipeId: string) => {
    const recipe = allUserRecipes.find(r => r.id === recipeId);
    if (recipe && (recipe.type === 'created' || recipe.type === 'variant')) {
      setRecipeToDelete({ id: recipeId, type: recipe.type });
      setShowDeleteDialog(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!recipeToDelete) {
      return;
    }
    
    try {
      if (recipeToDelete.type === 'variant') {
        await deleteVariant(recipeToDelete.id);
      } else {
        await deleteRecipe(recipeToDelete.id);
      }
      
      // Refresh the recipes list
      if (user) {
        fetchAllUserRecipes();
      }
    } catch (error) {
      console.error('Failed to delete recipe:', error);
    } finally {
      setShowDeleteDialog(false);
      setRecipeToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteDialog(false);
    setRecipeToDelete(null);
  };

  const handleCustomizeClick = (recipe: typeof allUserRecipes[0]) => {
    if (recipe.type === 'variant') {
      // For variant recipes, navigate to edit the existing variant
      navigate(`/variants/${recipe.id}/edit`, { 
        state: { recipe } 
      });
    } else {
      // For regular recipes, navigate to customize page to create a new variant
      navigate(`/recipes/${recipe.id}/customize`, { 
        state: { recipe } 
      });
    }
  };

  if (!user) {
    return (
      <div className={styles.container}>
        <Header user={user} onLogin={handleLogin} onLogout={logout} />
        <div className={styles.content}>
          <h1>Your Recipes</h1>
          <p>Please sign in to view your recipes.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Header user={user} onLogin={handleLogin} onLogout={logout} />
      <div className={styles.content}>
        <div className={styles.formSection}>
          <div className={styles.formHeader}>
            <div className={styles.headerContent}>
              <div className={styles.titleSection}>
                <h2 className={styles.formTitle}>Your Recipes</h2>
                <p className={styles.formDescription}>Quickly find recipes you've saved, created, or customized.</p>
              </div>
              <div className={styles.headerActions}>
                <button 
                  className={styles.createButton}
                  onClick={() => navigate('/recipes/new')}
                >
                  + Create Recipe
                </button>
              </div>
            </div>
          </div>
          <form className={styles.form} onSubmit={(e) => e.preventDefault()}>
            <div className={styles.inputGroup}>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filter by title or ingredient"
                className={styles.input}
              />
              {query && (
                <button type="button" className={styles.button} onClick={() => setQuery('')}>Clear</button>
              )}
            </div>
          </form>
        </div>
        
        {allUserRecipes.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>📖</div>
            <h2>No recipes yet</h2>
            <p>Start exploring recipes, save your favorites, or create your own to see them here!</p>
            <button 
              onClick={() => navigate('/recipes')} 
              className={styles.browseButton}
            >
              Browse Recipes
            </button>
          </div>
        ) : (
          <div className={styles.recipesGrid}>
            {allUserRecipes
              .filter(r => {
                if (!query.trim()) return true;
                const q = query.trim().toLowerCase();
                const inTitle = r.title.toLowerCase().includes(q);
                const inIngredients = r.ingredients.some(ing => (ing.name || '').toLowerCase().includes(q));
                return inTitle || inIngredients;
              })
              .map((recipe) => (
              <RecipeCard
                key={recipe.id}
                recipe={recipe}
                isSaved={recipe.type === 'saved'}
                onViewClick={handleRecipeClick}
                onUnsaveClick={recipe.type === 'saved' ? handleUnsaveClick : undefined}
                onCustomizeClick={handleCustomizeClick}
                onDeleteClick={handleDeleteClick}
                showSaveButton={false}
                showUnsaveButton={recipe.type === 'saved'}
                showCustomizeButton={true}
                showDeleteButton={recipe.type === 'created' || recipe.type === 'variant'}
              />
            ))}
          </div>
        )}

        {/* Recipe Detail Modal */}
        {selectedRecipe && (
          <div className={styles.modal} onClick={() => setSelectedRecipe(null)}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <h2>{selectedRecipe.title}</h2>
                <div className={styles.modalActions}>
                  <button
                    className={styles.customizeButton}
                    onClick={() => {
                      handleCustomizeClick(selectedRecipe);
                      setSelectedRecipe(null);
                    }}
                  >
                    Customize
                  </button>
                  <button
                    className={styles.closeButton}
                    onClick={() => setSelectedRecipe(null)}
                  >
                    ×
                  </button>
                </div>
              </div>
              
              <div className={styles.modalBody}>
                <section>
                  <h3>Ingredients</h3>
                  <ul>
                    {selectedRecipe.ingredients.map((item, idx) => (
                      <li key={idx}>
                        {item.quantity} {item.unit} {item.name}
                      </li>
                    ))}
                  </ul>
                </section>

                <section>
                  <h3>Instructions</h3>
                  <ol>
                    {selectedRecipe.instructions
                      .slice()
                      .sort((a, b) => a.order - b.order)
                      .map((step) => (
                        <li key={step.order}>{step.text}</li>
                      ))}
                  </ol>
                </section>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Dialog */}
        <ConfirmationDialog
          isOpen={showUnsaveDialog}
          title="Remove Recipe"
          message="Are you sure you want to remove this recipe from your saved recipes?"
          confirmText="Remove"
          cancelText="Cancel"
          onConfirm={handleUnsaveConfirm}
          onCancel={handleUnsaveCancel}
          type="danger"
        />

        {/* Delete Confirmation Dialog */}
        <ConfirmationDialog
          isOpen={showDeleteDialog}
          title="Delete Recipe"
          message="Are you sure you want to delete this recipe? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          type="danger"
        />
      </div>
    </div>
  );
};

export default Saved; 